import { StatusDropdown } from '../../components/ticket/StatusDropdown';
import { TypeIcon } from '../../components/ticket/TypeIcon';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { TicketListItem } from '../../api/types';
import { BoardCard } from './BoardCard';
import { ColumnDropZone } from './BoardColumnView';
import type { BoardColumn } from './lane-columns';
import { cardsInColumn, type Swimlane } from './swimlanes';
import { describeSubtaskCount } from './ticket-extras';

/**
 * One swimlane: the parent-story header row from shot 6 —
 * `⌄ APFS-441 [CASE-659] AI Internal MCP Server Phase 1 - Read only (18 subtasks) [PASSED ON UAT]` —
 * with the columns drawn beneath it.
 *
 * ⭐ The header carries the parent's OWN status control, because the parent is a header and not a
 * card: without it, the one ticket on the board you cannot move is the story itself.
 */
export function SwimlaneView({
  swimlane,
  columns,
  gridTemplate,
  collapsed,
  compact,
  dragTicketId,
  droppableKeys,
  refusalFor,
  onToggleCollapsed,
  onDropOn,
  onDragStart,
  onDragEnd,
  onOpen,
  onChanged,
}: {
  swimlane: Swimlane;
  columns: readonly BoardColumn[];
  gridTemplate: string;
  collapsed: boolean;
  /**
   * 🔴 THE PHONE. One lane is on screen, and a story with nothing IN that lane was rendering an
   * empty 96px drop zone — ~40% of a 390×844 screen given to a column holding nothing, pushing the
   * work below the fold. Found by rendering it at a real 390px; jsdom does no layout and could not
   * have seen it. ⛔ Not collapsed on desktop, where those empty cells are the drop targets.
   */
  compact: boolean;
  dragTicketId: string | null;
  /** null when no drag is in flight; otherwise the columns that will accept this card. */
  droppableKeys: Set<string> | null;
  refusalFor: (column: BoardColumn) => string | null;
  onToggleCollapsed: () => void;
  onDropOn: (column: BoardColumn) => void;
  onDragStart: (ticket: TicketListItem) => void;
  onDragEnd: () => void;
  onOpen: (ticket: TicketListItem) => void;
  onChanged: () => void;
}) {
  const vocab = useVocabulary();
  const parent = swimlane.parent;
  const shownHere = swimlane.tickets.length;
  /**
   * ⭐⭐ `APFS-441` has EIGHTEEN sub-tasks and this board may be holding two of them. The API's own
   * `child_count` is the number in the header; what this lane can draw is stated beside it. Printing
   * "(2 subtasks)" for a story Jira calls 18 is a confident wrong number.
   */
  const subtasks = parent ? describeSubtaskCount(parent, shownHere) : null;
  const emptyHere = columns.every((column) => cardsInColumn(swimlane, column.statuses).length === 0);

  return (
    <section
      aria-label={parent ? `Story ${parent.key}` : 'Work with no parent story'}
      className="border-b border-[color:var(--color-line)]"
    >
      {/*
        🔴 `sticky left-0` — THE HEADER LIVES INSIDE THE HORIZONTAL SCROLLER, and without this it is
        as wide as the board (six columns ≈ 1392px). At 1280 that put the story's own status control
        240px off the right edge: the one ticket you could not move was the story, and nothing on
        screen said why. Sticking it to the scrollport's left edge keeps the whole header visible at
        any scroll position — and it is what shot 6 shows, the header pinned left over the columns.
        ⚠️ WRAPS at 390px too: the status control took half the row and the key rendered as `APF…`,
        on a header whose whole job is to say WHICH story this is.
      */}
      <div className="sticky left-0 z-[1] flex w-max max-w-full flex-wrap items-center gap-x-2 gap-y-1 bg-[color:var(--color-surface)] px-2 py-1.5">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${parent ? parent.key : 'ungrouped work'}`}
          className="shrink-0 rounded px-1 text-sm text-[color:var(--color-ink-muted)] hover:bg-black/5 dark:hover:bg-white/10"
        >
          {collapsed ? '›' : '⌄'}
        </button>

        {parent ? (
          <>
            <TypeIcon type={vocab.type(parent.type)} size={14} />
            <button
              type="button"
              onClick={() => onOpen(parent)}
              className="min-w-0 max-w-[36rem] flex-[1_1_12rem] truncate text-left text-sm font-semibold hover:underline"
            >
              <span className="font-mono">{parent.key}</span> {parent.title}
            </button>
            <span
              title={
                subtasks?.isExact
                  ? 'Counted by the API over the whole project'
                  : 'The API did not send a child count — this is what the board is holding'
              }
              className="shrink-0 text-xs text-[color:var(--color-ink-muted)]"
            >
              ({subtasks?.text})
            </span>
            {/* The story's own status — the header is the only place it appears. */}
            <StatusDropdown
              ticketId={parent.id}
              statusCode={parent.status}
              onChanged={onChanged}
              compact
            />
          </>
        ) : (
          <>
            <span className="truncate text-sm font-semibold">No parent story</span>
            <span className="shrink-0 text-xs text-[color:var(--color-ink-muted)]">
              ({shownHere} {shownHere === 1 ? 'item' : 'items'})
            </span>
          </>
        )}
      </div>

      {collapsed ? null : emptyHere && compact ? (
        /* One line instead of an empty column. ⭐ It still says WHICH lane is empty — "nothing here"
           with no lane named would read as "this story has no work at all". */
        <p className="px-3 pb-2 text-xs text-[color:var(--color-ink-muted)]">
          Nothing in {columns[0]?.laneLabel ?? 'this lane'}.
        </p>
      ) : (
        <div className="grid items-start gap-2 p-2" style={{ gridTemplateColumns: gridTemplate }}>
          {columns.map((column) => {
            const cards = cardsInColumn(swimlane, column.statuses);
            return (
              <ColumnDropZone
                key={column.key}
                column={column}
                droppable={droppableKeys === null ? null : droppableKeys.has(column.key)}
                refusalReason={refusalFor(column)}
                onDrop={() => onDropOn(column)}
              >
                {cards.map((ticket) => (
                  <BoardCard
                    key={ticket.id}
                    ticket={ticket}
                    detached={swimlane.detachedIds.has(ticket.id)}
                    dragging={dragTicketId === ticket.id}
                    draggable
                    onDragStart={() => onDragStart(ticket)}
                    onDragEnd={onDragEnd}
                    onOpen={() => onOpen(ticket)}
                    onChanged={onChanged}
                  />
                ))}
              </ColumnDropZone>
            );
          })}
        </div>
      )}
    </section>
  );
}
